import { filter, groupBy, isEmpty, keys, reduce, map, flatMap } from 'lodash'
import moment from 'moment'

import { MENU_TYPES } from './menuTypes'

export const PREFERENCES_MAP = {
  flags: {
    type: 'Customer::FlagsPreference',
    attributes: ['lockRefrigerator', 'lockLiquorCabinet', 'presentBill', 'shouldVisit', 'taxExempt'],
  },
  gratuity: {
    type: 'Customer::GratuityPreference',
    attributes: ['gratuity', 'gratuityIsAdjustable', 'gratuityIsPercentage', 'maxGratuity'],
  },
  notes: {
    type: 'Customer::NotesPreference',
    attributes: ['discountDescription', 'host', 'notes', 'posNotes'],
  },
  payment: {
    type: 'Customer::PaymentPreference',
    attributes: [], // no attributes, just use this to grab customer_payment_id
  },
  receipt: {
    type: 'Customer::ReceiptPreference',
    attributes: ['provideReceipt', 'receiptEmail'],
  },
}

export const shouldVisitDropdownOptions = () => {
  const valueMap = {
    true: 'Visit Always',
    false: 'Visit Never',
    "": 'Visit Upon Request',
  }

  return keys(valueMap).map((value) => {
    return { value: value, displayName: valueMap[value] }
  })
}

const getLatestPref = (prefs) =>
  reduce(prefs, (mostRecentPref, pref) =>
    moment(pref?.updatedAt).isAfter(moment(mostRecentPref?.updatedAt))
      ? pref
      : mostRecentPref
  )

export const getDefaultNonSuiteCustomerPreference = (defaults, preferences, customerId, customerPayments) => {
  const defaultPreferences = map(defaults?.[customerId]?.noStand, (id) => preferences[id] )
  const latestPref = getLatestPref(defaultPreferences) || {}
  return makeCustomerPreferences([latestPref], customerPayments, null )
}

const getMostSpecificNonEventPreference = (preferences, standUuid) => {
  if (isEmpty(preferences)) return {}

  let nonEventPreferences = filter(preferences, (pref) => !pref.salesEventId)
  let prefs = []

  prefs = filter(nonEventPreferences, (pref) => pref.standUuid === standUuid)
  if (!isEmpty(prefs)) return getLatestPref(prefs) || {}

  prefs = filter(nonEventPreferences, (pref) => !pref.standUuid)
  return getLatestPref(prefs) || {}
}

const getMostSpecificPreference = (preferences, currentEventId, currentStandId) => {
  if (isEmpty(preferences)) return {}

  let prefs = []
  prefs = filter(preferences, (pref) => !!pref.salesEventId && pref.salesEventId === currentEventId && !!pref.standUuid && pref.standUuid === currentStandId)
  if (!isEmpty(prefs)) return getLatestPref(prefs) || {}

  prefs = filter(preferences, (pref) => !!pref.salesEventId && pref.salesEventId === currentEventId)
  if (!isEmpty(prefs)) return getLatestPref(prefs) || {}

  prefs = filter(preferences, (pref) => !pref.salesEventId && pref.standUuid)
  if (!isEmpty(prefs)) return getLatestPref(prefs) || {}

  prefs = filter(preferences, (pref) => !pref.standUuid && !pref.salesEventId)
  return getLatestPref(prefs) || {}
}

export const getMostSpecificNonEventMenuPreference = (preferences, menuType, standUuid) => {
  if (isEmpty(preferences)) return {}

  let menuTypePrefs = filter(preferences, (pref) => pref.menuSubtype === menuType)

  // if no matches, want to also exclude preferences for other menu types
  if (isEmpty(menuTypePrefs)) menuTypePrefs = filter(preferences, (pref) => !pref.menuSubtype)

  return getMostSpecificNonEventPreference(menuTypePrefs, standUuid)
}

const getMostSpecificMenuPreference = (preferences, menuType, currentEventId, currentStandId) => {
  if (isEmpty(preferences)) return {}

  let menuTypePrefs = filter(preferences, (pref) => pref.menuSubtype === menuType)

  // if no matches, want to also exclude preferences for other menu types
  if (isEmpty(menuTypePrefs)) menuTypePrefs = filter(preferences, (pref) => !pref.menuSubtype)

  return getMostSpecificPreference(menuTypePrefs, currentEventId, currentStandId)
}

const makeNonMenuPreferences = (groupedPreferences, currentEventId) => {
  if (isEmpty(groupedPreferences)) return {}

  const preferenceKeys = ['flags', 'notes', 'receipt']

  const combinedPreferences = reduce(preferenceKeys, (result, preferenceKey) => {
    const preferenceMap = PREFERENCES_MAP[preferenceKey]
    const preferenceGroup = groupedPreferences[preferenceMap.type]
    const nonMenuGroup = filter(preferenceGroup, ['menuSubtype', null])
    const mostSpecific = getMostSpecificPreference(nonMenuGroup, currentEventId)

    preferenceMap.attributes.map(attr => result[attr] = mostSpecific[attr])

    return result
  }, {})

  return combinedPreferences
}

const makeMenuPreferences = (groupedPreferences, payments, currentEventId, currentStandId) => {
  const menuTypes = keys(MENU_TYPES)
  const preferenceKeys = ['flags', 'gratuity', 'payment']

  const combinedPreferences = reduce(menuTypes, (result, menuType) => {
    result[menuType] = {}

    preferenceKeys.forEach((key) => {
      const preferenceMap = PREFERENCES_MAP[key]
      const preferenceGroup = groupedPreferences[preferenceMap.type]
      const mostSpecific = getMostSpecificMenuPreference(preferenceGroup, menuType, currentEventId, currentStandId)

      let menuResult = {}

      if (key === 'gratuity') {
        menuResult = reduce(preferenceMap.attributes, (menuPrefs, attr) => {
          menuPrefs[attr] = mostSpecific[attr]

          return menuPrefs
        }, {})
      }

      if (key === 'payment') {
        if(currentEventId) {
          // If the most specific preference is not an event preference or it has no payments, then
          // mark no payments as selected; otherwise, mark those payments as selected which have been
          // chosen for this event.
          if ((currentEventId && !mostSpecific.salesEventId) || !mostSpecific.customerPaymentIds) {
            menuResult.payments = []
          } else {
            menuResult.payments = payments.filter(payment => mostSpecific.customerPaymentIds.includes(payment.uuid)) || []
          }
        } else {
          const customerPaymentIds = flatMap(filter(preferenceGroup, (pref) => (pref.menuSubtype === menuType || !pref.menuSubtype) && !pref.salesEventId), (pref) => pref.customerPaymentIds || [])
          menuResult.payments = payments.filter(payment => customerPaymentIds.includes(payment.uuid)) || []
        }
      }

      if (key === 'flags') menuResult.shouldVisit = mostSpecific.shouldVisit

      result[menuType] = { ...result[menuType], ...menuResult }
    })

    return result
  }, {})

  return combinedPreferences
}

export const makeCustomerPreferences = (preferences, payments, currentEventId, currentStandId) => {
  const byType = groupBy(preferences, 'type')

  const nonMenuPreferences = makeNonMenuPreferences(byType, currentEventId)
  const menuPreferences = makeMenuPreferences(byType, payments, currentEventId, currentStandId)

  return {
    ...nonMenuPreferences,
    ...menuPreferences,
  }
}

export default makeCustomerPreferences
